import React, { useEffect, useRef } from "react";
import { size } from "lodash";
import ReactDOMServer from "react-dom/server";
import { createGlobalStyle } from "styled-components";
import styled from "styled-components";
import Treemap, { TreemapChartInstance } from "treemap-chart";
import {
  Color,
  FontFamily,
  FontSize,
  FontWeight,
  Radius,
  Spacing,
} from "@glossy";
import { background } from "@glossy";
import { Locale } from "@linkfluence/models/locale";
import { Metric } from "@linkfluence/models/metric";
import { useSize } from "@lkf/hooks";
import { useComputedSeries } from "../data";
import { Data } from "../model";
import { getTooltipContent, getTooltipTitle } from "../tooltip";

const GlobalStyle = createGlobalStyle`
  .treemap-viz text {
    font-family: ${FontFamily.Sans};
    font-size: ${FontSize.Small};
    font-weight: ${FontWeight.Normal};
    fill: ${Color.White};
  }
  .treemap-viz text .text-contour {
    fill: none;
    stroke: none;
  }
  .treemap-tooltip {
    background: ${Color.BlackPearl};
    padding: ${Spacing.MediumLarge};
    border-radius: ${Radius.Medium};
    font-family: ${FontFamily.Sans};
    font-size: ${FontSize.Medium};
    z-index: 999;
  }

  .treemap-viz rect {
    stroke: ${background};
  }
`;

const ChartWrapper = styled.div`
  height: 100%;
  width: 100%;
  cursor: pointer;
`;

export interface IProps {
  series: Data;
  metric: Metric;
  locale: Locale;
  onClickBranch?: (id: string) => void;
  disableAnimation?: boolean;
}

const TreemapChart = ({
  series,
  onClickBranch,
  metric,
  locale,
  disableAnimation = false,
}: IProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const chart = useRef<TreemapChartInstance>(null);
  const { width, height } = useSize(ref);
  const computedSeries = useComputedSeries(series);
  useEffect(() => {
    if (ref.current && !chart.current) {
      chart.current = Treemap()
        .excludeRoot(true)
        // .label("label")
        .label((d: any) => {
          return !size(d.children) ? d.label : "";
        })
        .color("color")
        .size("vizSize")
        .zoomBy(null)
        .transitionDuration(disableAnimation ? 0 : 800)
        .tooltipTitle((node) =>
          ReactDOMServer.renderToString(getTooltipTitle(node))
        )
        .tooltipContent((node) => getTooltipContent(node, metric, locale))
        .onClick((node: any) => node && onClickBranch(node.uid))(ref.current);
    }
  }, [
    ref,
    computedSeries,
    metric,
    onClickBranch,
    height,
    locale,
    disableAnimation,
  ]);

  useEffect(() => {
    if (chart.current) {
      chart.current.width(width).data(computedSeries).height(height);
    }
  }, [chart, width, computedSeries, height]);

  useEffect(() => {
    const handleZoom = (e) => {
      e.stopImmediatePropagation();
      return false;
    };
    ref.current
      ?.querySelector("svg")
      .addEventListener("wheel", handleZoom, true);
  }, []);

  useEffect(() => {
    if (chart.current) {
      chart.current.width(width).data(computedSeries).height(height);
    }
  }, [chart, width, computedSeries, height]);

  return (
    <>
      <GlobalStyle />
      <ChartWrapper ref={ref} />
    </>
  );
};

export default TreemapChart;
